import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Loader2, Sparkles } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { lottieService, LottieResult } from '../../services/lottieService';
import { cn } from '../../lib/utils';

interface LottieSearchPanelProps {
  onSelect: (animation: LottieResult) => void;
  defaultQuery?: string;
  /** External search query from parent (for unified search) */
  externalQuery?: string;
  /** Callback when search query changes (for unified search) */
  onQueryChange?: (query: string) => void;
}

export const LottieSearchPanel: React.FC<LottieSearchPanelProps> = ({
  onSelect,
  defaultQuery = '',
  externalQuery,
  onQueryChange,
}) => {
  // Search state - use external query if provided
  const [internalQuery, setInternalQuery] = useState(defaultQuery);
  const searchQuery = externalQuery !== undefined ? externalQuery : internalQuery;

  const setSearchQuery = (query: string) => {
    if (onQueryChange) {
      onQueryChange(query);
    } else {
      setInternalQuery(query);
    }
  };
  const [results, setResults] = useState<LottieResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnimation, setSelectedAnimation] = useState<LottieResult | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const currentQueryRef = useRef<string>('');

  // Debounce timer ref
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Infinite scroll refs
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Search function - fetches pages 1 & 2 in parallel for richer initial results
  const searchAnimations = useCallback(async (query: string) => {
    setLoading(true);
    setError(null);
    setResults([]);
    setCurrentPage(0);
    setHasMore(false);
    currentQueryRef.current = query;

    try {
      if (!query.trim()) {
        // For empty query, get popular animations
        const response = await lottieService.getPopularAnimations();
        if (response.success) {
          setResults(response.animations);
          setTotalResults(response.totalMatches);
          setHasMore(false); // Popular doesn't support pagination
        } else {
          setError(response.error || 'Failed to load animations');
        }
      } else {
        // Parallel fetch pages 1 & 2 for 40 initial results
        const [page1, page2] = await Promise.all([
          lottieService.searchAnimations(query, 20, 1),
          lottieService.searchAnimations(query, 20, 2)
        ]);

        if (page1.success) {
          const combined = [...page1.animations, ...(page2.success ? page2.animations : [])];
          setResults(combined);
          setTotalResults(combined.length);
          setCurrentPage(2);
          // Has more if page 2 returned full results
          setHasMore(page2.success && page2.hasMore === true);
        } else {
          setError(page1.error || 'Failed to load animations');
        }
      }
    } catch (err) {
      setError('Failed to load animations');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load next page for infinite scroll
  const loadNextPage = useCallback(async () => {
    if (loadingMore || !hasMore || !currentQueryRef.current.trim()) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const response = await lottieService.searchAnimations(currentQueryRef.current, 20, nextPage);

      if (response.success) {
        setResults(prev => [...prev, ...response.animations]);
        setTotalResults(prev => prev + response.animations.length);
        setCurrentPage(nextPage);
        setHasMore(response.hasMore === true);
      }
    } catch (err) {
      console.error('[LottieSearchPanel] Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, currentPage]);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchAnimations(searchQuery);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, searchAnimations]);

  // Initial load
  useEffect(() => {
    searchAnimations('');
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          loadNextPage();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    // Observe the sentinel element
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, loadNextPage]);

  // Handle animation click
  const handleAnimationClick = (animation: LottieResult) => {
    setSelectedAnimation(animation);
    onSelect(animation);
  };

  // Handle search on Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchAnimations(searchQuery);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <div className="border-b border-border px-3 py-2 bg-background flex-shrink-0 space-y-2">
        {/* Row 1: Search Input */}
        <div className="flex gap-2 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search animations (e.g., loading, success, rocket)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-8 pr-3 h-8 text-sm"
              autoFocus
            />
          </div>
          <Button
            onClick={() => searchAnimations(searchQuery)}
            disabled={loading}
            size="sm"
            className="h-8 px-3"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {/* Row 2: Results Count */}
        <div className="flex items-center gap-3 flex-wrap">
          {totalResults > 0 && (
            <div className="text-[10px] text-muted-foreground ml-auto">
              {results.length} animation{results.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Results Grid */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-2 py-2 min-h-0">
        {error ? (
          <div className="text-center py-8">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
              <Sparkles className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">
              {searchQuery ? 'No animations found. Try a different search term.' : 'Start typing to search animations'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-1.5">
              {results.map((animation) => (
                <div
                  key={animation.id}
                  className={cn(
                    "relative group cursor-pointer rounded-lg overflow-hidden transition-all",
                    selectedAnimation?.id === animation.id
                      ? "ring-2 ring-primary"
                      : "hover:ring-1 hover:ring-primary/50 border border-transparent hover:border-border"
                  )}
                  onClick={() => handleAnimationClick(animation)}
                  title={`${animation.name} by ${animation.author}`}
                >
                  {/* Animation Preview */}
                  <div className="aspect-square bg-muted/30 flex items-center justify-center overflow-hidden">
                    <img
                      src={animation.previewGif || animation.previewImage}
                      alt={animation.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>

                  {/* Animation Name on hover */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[9px] text-white truncate text-center">
                      {animation.name}
                    </p>
                  </div>

                  {/* Selection indicator */}
                  {selectedAnimation?.id === animation.id && (
                    <div className="absolute top-1 right-1">
                      <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                        <Sparkles className="h-2.5 w-2.5 text-primary-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Infinite scroll sentinel and loading indicator */}
            {hasMore && (
              <div
                ref={loadMoreRef}
                className="flex items-center justify-center py-4"
              >
                {loadingMore && (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer hint */}
      <div className="flex-shrink-0 border-t border-border px-3 py-1.5 bg-muted/30">
        <p className="text-[10px] text-muted-foreground text-center">
          Click an animation to preview it
        </p>
      </div>
    </div>
  );
};

export default LottieSearchPanel;
